import React, { useContext, useState } from 'react';
import { Button, Select, Input, Alert, message } from 'antd';
import config from '$config';
import { CancelAction, StyledInvitationCancellationModal } from '../styled';
import { patchBooking as patchBookingRequest, patchJobInvitation as patchJobInvitationRequest } from '../api';
import BookingPageContext from '../../context';

const { TextArea } = Input;
const { Option } = Select;

const getErrorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

function InvitationCancellationModal({
  invitationStatus,
  invitationId,
  bookingId,
  garageInvites,
  garageName,
}) {
  const { reloadBooking } = useContext(BookingPageContext);
  const [showModal, setShowModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState(undefined);
  const [cancellationComment, setCancellationComment] = useState(undefined);

  const closeAndClearModal = () => {
    setCancellationComment(undefined);
    setCancellationReason(undefined);
    setShowModal(false);
  };

  const shouldCancelInvitationAndNotAlterBooking = () => {
    const activeInvites = garageInvites.filter((invite) => ['pending', 'review'].includes(invite.status));
    return invitationStatus !== 'accepted' && (invitationStatus === 'draft' || activeInvites?.length > 1);
  };

  const confirmCancelInvitation = async () => {
    message.info('Cancelling job invitation...');

    try {
      if (shouldCancelInvitationAndNotAlterBooking()) {
        await patchJobInvitationRequest(invitationId, { status: 'cancelled' });
      } else {
        await patchBookingRequest(bookingId, {
          masterStatusId: 10000,
          allocationStatus: 'unallocated',
          cancellationReason,
          cancellationComment,
        });
      }
    } catch (error) {
      // Keep the modal open so the user can retry or adjust their input.
      message.error(getErrorMessage(error, 'Unable to cancel job invitation'));
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const urlParamsJobInvitationId = urlParams.get('jobInvitationId');

    if (invitationId === urlParamsJobInvitationId) {
      const urlWithoutParams = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, urlWithoutParams);
    }

    closeAndClearModal();
    reloadBooking();
  };

  const allCancellationReasons = [
    { reason: 'Garage too far for Fixter driver' },
    { reason: 'Too far for garage collection' },
    { reason: 'Too far for customer to drop-off' },
    { reason: 'Need to change booking c&d type' },
    { reason: 'This garage does bad work' },
    { reason: "Garage doesn't have capacity" },
    { reason: 'Garage is too expensive' },
    { reason: "Garage doesn't support operation" },
    { reason: "Garage doesn't support this vehicle" },
    { reason: 'Customer not happy with garage' },
    { reason: "Garage doesn't want the booking", commentRequired: true },
    { reason: 'Other', commentRequired: true },
  ];

  const renderCancellationOptions = () =>
    allCancellationReasons.map(({ reason }) => {
      const key = reason.replace(/s/g, '_');
      return (
        <Option key={key} value={reason}>
          {reason}
        </Option>
      );
    });

  const CancellationReasonSelect = () => (
    <Select
      value={cancellationReason || undefined}
      onChange={(event) => setCancellationReason(event)}
      style={{ width: '100%' }}
      placeholder="Select a cancellation reason"
    >
      {renderCancellationOptions()}
    </Select>
  );

  const doesReasonRequireComment = () => {
    const matchedReason = allCancellationReasons.find(({ reason }) => reason === cancellationReason);
    return matchedReason?.commentRequired;
  };

  const reasonRequiresComment = doesReasonRequireComment(cancellationReason);

  const isSubmitButtonDisabled = () => {
    if (!cancellationReason) return true;
    if (reasonRequiresComment && !cancellationComment) return true;
    return false;
  };

  const RequiredLabel = () => <i className={isSubmitButtonDisabled && 'text-red'}>(required)</i>;
  return (
    <div>
      <CancelAction onClick={() => setShowModal(true)}>Cancel invite</CancelAction>

      <StyledInvitationCancellationModal
        title={`Cancel Job invitation for ${garageName}`}
        visible={showModal}
        onOk={confirmCancelInvitation}
        onCancel={closeAndClearModal}
        footer={[
          <Button
            disabled={isSubmitButtonDisabled()}
            key="submit"
            type="primary"
            onClick={confirmCancelInvitation}
          >
            Confirm Cancellation
          </Button>,
        ]}
      >
        <Alert
          message="If a garage has already accepted the invitation, we&#39;ll send them an email to let
          them know the job has been cancelled."
          type="info"
        />
        <p>
          Reason <RequiredLabel />
        </p>
        <CancellationReasonSelect />

        <p>Additional comments {reasonRequiresComment && <RequiredLabel />}</p>
        <TextArea
          value={cancellationComment}
          onChange={(e) => setCancellationComment(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 4 }}
          style={{ width: '100%' }}
        />
      </StyledInvitationCancellationModal>
    </div>
  );
}

export default InvitationCancellationModal;
